import { useEffect, useRef, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { Camera, RotateCcw, Check, Loader2, AlertTriangle } from "lucide-react";
import { useFaceAlign, FACE_HINTS } from "@/lib/faceAlign";

// Returns null while loading, true if this room still needs an ID photo.
export function usePhotoNeeded(token, source) {
  const [needed, setNeeded] = useState(null);
  useEffect(() => {
    api.get(`/invite/${token}`).then(r => {
      const ps = r.data?.candidate?.photo_source;
      setNeeded(source === "video" ? ps !== "video" : !ps);
    }).catch(() => setNeeded(true));
  }, [token, source]);
  return [needed, () => setNeeded(false)];
}

const FaceGuide = ({ ok }) => (
  <svg viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full pointer-events-none">
    <defs>
      <mask id="face-hole"><rect width="400" height="300" fill="white" /><ellipse cx="200" cy="140" rx="78" ry="104" fill="black" /></mask>
    </defs>
    <rect width="400" height="300" fill="rgba(2,6,23,0.55)" mask="url(#face-hole)" />
    <ellipse cx="200" cy="140" rx="78" ry="104" fill="none" stroke={ok ? "#34D399" : "#FFFFFF"} strokeWidth="3" strokeDasharray={ok ? "0" : "8 6"} />
  </svg>
);

export default function PhotoCheck({ token, source, onDone }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [camErr, setCamErr] = useState(null);
  const [count, setCount] = useState(0);
  const [shot, setShot] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [cancelled, setCancelled] = useState(false);
  const face = useFaceAlign(videoRef, !shot && !camErr);
  const faceRef = useRef(face);
  faceRef.current = face;
  const noDetector = face === "unavailable";
  const aligned = face === "ok";
  const canCapture = noDetector || aligned;

  const openCam = async () => {
    setCamErr(null);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { width: 1280, height: 720, facingMode: "user" }, audio: false });
      streamRef.current = s;
      if (videoRef.current) videoRef.current.srcObject = s;
    } catch (e) {
      setCamErr(e?.name === "NotAllowedError" ? "Camera access was blocked. Allow the camera for this site in your browser settings, then try again." : "We couldn't start your camera. Make sure no other app is using it, then try again.");
    }
  };
  const stopCam = () => { streamRef.current?.getTracks().forEach(t => t.stop()); streamRef.current = null; };

  useEffect(() => { openCam(); return stopCam; }, []);
  useEffect(() => { if (!shot && videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current; }, [shot]);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const w = 640, h = Math.round((v.videoHeight / v.videoWidth) * w);
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(v, 0, 0, w, h);
    c.toBlob(b => b && setShot({ blob: b, url: URL.createObjectURL(b) }), "image/jpeg", 0.88);
  };

  const capture = () => {
    let n = 3;
    setCancelled(false);
    setCount(n);
    const t = setInterval(() => {
      const f = faceRef.current;
      if (f !== "unavailable" && f !== "ok" && f !== "settling") {
        clearInterval(t); setCount(0); setCancelled(true); return;
      }
      n -= 1;
      if (n === 0) { clearInterval(t); setCount(0); snap(); } else setCount(n);
    }, 1000);
  };

  const retake = () => { if (shot) URL.revokeObjectURL(shot.url); setShot(null); setSaveErr(null); };

  const confirm = async () => {
    setSaving(true); setSaveErr(null);
    try {
      const fd = new FormData();
      fd.append("file", shot.blob, "photo.jpg");
      await api.post(`/candidate-photo/${token}?source=${source}`, fd);
      stopCam();
      onDone();
    } catch (e) {
      setSaveErr(fmtErr(e.response?.data?.detail) || "Couldn't save your photo. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-[#0A0D14]/95 backdrop-blur-sm p-4 overflow-y-auto" data-testid="photo-check">
      <div className="tp-card p-6 sm:p-8 w-full max-w-lg">
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Step 1 of 2</div>
        <div className="font-display text-xl font-semibold mb-1">Photo check</div>
        <div className="text-sm text-slate-400 mb-5">A photo is required for identity verification before your {source === "video" ? "video interview" : "coding interview"} begins.</div>

        <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden bg-black mb-4" data-testid="photo-frame">
          {shot ? (
            <img src={shot.url} alt="Your photo" className="w-full h-full object-cover -scale-x-100" data-testid="photo-preview" />
          ) : (
            <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover -scale-x-100" data-testid="photo-camera" />
          )}
          {!camErr && <FaceGuide ok={!!shot || aligned} />}
          {count > 0 && (
            <div className="absolute inset-0 grid place-items-center" data-testid="photo-countdown">
              <span key={count} className="font-display text-7xl font-bold text-white drop-shadow-lg">{count}</span>
            </div>
          )}
          {camErr && (
            <div className="absolute inset-0 grid place-items-center p-6 text-center" data-testid="photo-cam-error">
              <div>
                <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <div className="text-sm text-slate-200">{camErr}</div>
              </div>
            </div>
          )}
        </div>

        {!shot && !camErr && (
          <div className="text-center mb-4">
            <div className="text-sm text-slate-300" data-testid="photo-instructions">
              Align your face inside the outline, look at the camera and <b>hold still</b>.
            </div>
            {!noDetector && (
              <div data-testid="face-hint" data-status={face} className={`mt-2 inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${aligned ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-amber-300"}`}>
                {face === "loading" && <Loader2 className="w-3 h-3 animate-spin" />}
                {cancelled && !aligned ? "Countdown stopped — re-align your face" : FACE_HINTS[face]}
              </div>
            )}
          </div>
        )}
        {shot && <div className="text-sm text-slate-300 text-center mb-4">Is your face clear and inside the outline?</div>}
        {saveErr && <div className="text-sm text-rose-400 text-center mb-3" data-testid="photo-save-error">{saveErr}</div>}

        {camErr ? (
          <button data-testid="photo-retry-camera" onClick={openCam} className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium inline-flex items-center justify-center gap-2">
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
        ) : !shot ? (
          <button data-testid="photo-capture" onClick={capture} disabled={count > 0 || !canCapture} className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <Camera className="w-4 h-4" /> {count > 0 ? "Hold still…" : "Capture"}
          </button>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="photo-retake" onClick={retake} disabled={saving} className="py-3 rounded-lg border border-[color:var(--bd-hi)] text-slate-200 hover:bg-white/5 inline-flex items-center justify-center gap-2">
              <RotateCcw className="w-4 h-4" /> Retake
            </button>
            <button data-testid="photo-confirm" onClick={confirm} disabled={saving} className="py-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Use this photo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
